import React, { useEffect, useMemo, useRef, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import {
  Check,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  FileUp,
  Pencil,
  Plus,
  Send,
  Split,
  Trash2,
  CheckCircle2,
  RotateCcw,
  X,
  Ban,
} from 'lucide-react';
import { db } from '../../utils/firebase';
import { subscribeProjectFileStreams } from '../../utils/projectFileService';
import { rfqStructureNodeSets } from '../../utils/rfqProjectStructures';
import { packageHubReleaseIds } from '../../utils/rfqPackageReleases';
import { useAuth } from '../../utils/AuthContext';
import { useCRM } from '../../utils/CRMContext';
import { getUserInitials } from '../../utils/avatarUtils';
import {
  canShowCombinedQuoteSplitEntry,
} from '../../utils/combinedQuoteSplit';
import { canApproveSupplierQuoteOnBehalf } from '../../utils/staffSupplierQuoteAccess';
import CombinedQuoteSplitModal from './CombinedQuoteSplitModal';
import './CombinedQuoteSplitModal.css';
import type {
  RfqHubFileRelease,
  RfqPackage,
  RfqProject,
  RfqSubmission,
  SupplierInvite,
  SupplierOrg,
  TimberSupplierInvite,
  VendorWorkType,
} from '../../types';
import type { WorkTypeVendorSummary } from '../../utils/vendorAdminProjectSummary';
import {
  allSelectableTimberSpecies,
  inferQuotedSpeciesFromFileName,
  submissionQuotedDryness,
  submissionQuotedSpecies,
} from '../../utils/timberRfqSuppliers';
import { subscribeToTimberItems, type TimberItem } from '../../utils/materialsDatabaseService';
import {
  TIMBER_SUPPLIER_SURFACE_SPEC,
  timberRfqDrynessSpecLabel,
  timberSupplierDimensionsSpecForLists,
} from '../../utils/supplierTimberExport';
import { formatQuoteTotal, submissionQuoteTotal } from '../../utils/rfqQuoteTotal';
import { rfqSubmissionDeleteLabel } from '../../utils/otherQuoteUpload';
import { formatSubmissionLeadTimeWeeks } from '../../utils/rfqLeadTimeWeeks';
import { buildRfqSupplierSaveUpdates, supplierOrgIdsForReleaseSelection } from '../../utils/rfqProjectSuppliers';
import { rfqProjectSupplierShipTo, shipToContextForWorkType, type ProjectSiteAddressInput } from '../../utils/supplierShipTo';
import { supplierStructureScopeBadgeLabel } from '../../utils/timberRfqStructureScope';
import { activeScopeStructures } from '../../utils/rfqPackageArchivedStructures';
import { packageScopeDiffersFromInvite } from '../../utils/rfqPackageScopeDrift';
import { isReferenceRelease } from '../../utils/rfqReleasePurpose';
import { groupQuoteSubmissionHistory, siblingSubmissionIdsInQuoteStream } from '../../utils/rfqSubmissionHistory';
import {
  approvalClearIdsForTimberQuote,
  timberAssignedStructuresFullyCovered,
  timberQuoteAssignedStructureIds,
  timberQuoteStreamKey,
  timberQuoteAssignableStructures,
  timberQuoteStructureScopeDisplayLabel,
} from '../../utils/timberQuoteStreamKey';
import {
  primarySubmissionsFromGroups,
  resolveQuoteStreamDisplay,
  visibleQuoteRowsNewestFirst,
} from '../../utils/vendorPortalQuoteDisplay';
import SupplierShipToLine from './SupplierShipToLine';
import {
  canInternalEditQuoteSubmission,
  canStaffEditQuoteSubmission,
  isLegacyPrePortalSubmission,
  isStaffManualUploadSubmission,
  staffManualUploadBadgeLabel,
} from '../../utils/rfqSubmissionUploadLabel';
import {
  filterTimberPackagesForReleaseSelection,
  PRE_PORTAL_RELEASE_ID,
  approvedSubmissionIdsForReleaseSelection,
  buildTimberApprovalUpdate,
} from '../../utils/timberRfqPrePortalVersion';
import LegacyQuoteEditModal from './LegacyQuoteEditModal';
import { PmSubmissionQuoteInputAdjustedBadge } from './PmQuoteInputAdjustedBadge';
import ScopeChangeDismissConfirmModal from './ScopeChangeDismissConfirmModal';
import RfqQuoteDeleteConfirmModal from './RfqQuoteDeleteConfirmModal';
import StaffSupplierQuoteUploadModal from './StaffSupplierQuoteUploadModal';
import SupplierQuoteApprovalModal, { type SupplierQuoteApprovalTarget } from './SupplierQuoteApprovalModal';
import SupplierPaymentTermsModal from './SupplierPaymentTermsModal';
import TimberRfqSuppliersModal from './TimberRfqSuppliersModal';
import {
  recordSupplierBillForRfqAward,
  retractSupplierBillForRfqAward,
} from '../../utils/supplierBillIngestion';
import { findSupplierBillForRfqAward, setSupplierBillPaymentTerms } from '../../utils/supplierBillsService';
import type { SupplierPaymentTermsSpec } from '../../utils/supplierBills';
import {
  buildQuoteRejectionTargets,
  isRfqQuoteRejected,
  timberQuoteDeclineTarget,
  type QuoteRejectionTarget,
} from '../../utils/rfqQuoteRejectionTargets';
import { useSupplierBills } from '../../hooks/useSupplierBills';
import {
  buildApprovedQuotePaymentTermsTarget,
  findAwardBillForSubmission,
  resolveApprovedQuoteBannerMeta,
  timberQuoteApprovedAt,
  timberQuoteApproverInitials,
} from '../../utils/approvedQuoteBanner';
import ApprovedQuoteBanner from './ApprovedQuoteBanner';
import './TimberRfqSuppliersPanel.css';

function activeQuoteSubmissions(subs: RfqSubmission[]): RfqSubmission[] {
  return subs
    .filter(s => !s.notBidding && (s.storagePath || s.cncQuote))
    .sort((a, b) => {
      const aSpecies = a.quotedSpecies ?? a.fileName ?? '';
      const bSpecies = b.quotedSpecies ?? b.fileName ?? '';
      const byName = aSpecies.localeCompare(bSpecies, undefined, { sensitivity: 'base' });
      if (byName !== 0) return byName;
      const aTime = a.createdAt instanceof Date ? a.createdAt.getTime() : 0;
      const bTime = b.createdAt instanceof Date ? b.createdAt.getTime() : 0;
      return bTime - aTime;
    });
}

export interface TimberRfqSuppliersPanelProps {
  project: RfqProject;
  projectSupplierOrgIds: string[];
  workSummary: WorkTypeVendorSummary;
  supplierOrgById: Map<string, SupplierOrg>;
  timberCapableOrgs: SupplierOrg[];
  projectSite: ProjectSiteAddressInput;
  supplierInvites: SupplierInvite[];
  projectNumber?: string;
  projectName?: string;
  packages: RfqPackage[];
  submissionsByPackageId: Map<string, RfqSubmission[]>;
  selectedReleaseId: string | null;
  hubReleases?: RfqHubFileRelease[];
  canStaffUploadQuote?: boolean;
  publishBusy?: boolean;
  /** Send quote request email for the selected release to suppliers without a package yet. */
  onSendQuoteRequest?: (orgIds: string[]) => void;
  /** Resend after the supplier's structure scope changed on an already-published package. */
  onResendScopeChange?: (orgId: string) => void;
  /** Dismiss scope-change reminder without emailing the supplier. */
  onDismissScopeChange?: (orgId: string) => Promise<boolean>;
  /** Supplier org ids that can be declined (pre-portal estimates, or losers after an award). */
  rejectionTargetOrgIds?: ReadonlySet<string>;
  onRejectQuote?: (orgId: string) => void;
  onRequestRejectionNotices?: (targets: QuoteRejectionTarget[], afterClose?: () => void) => void;
  /** CNC RFQ on the same deal — affects delivery address for dual-work-type suppliers. */
  cncRfqProject?: RfqProject | null;
  /** Project Hub PM uid — same gate as quote approve for split commit. */
  hubProjectManagerUid?: string;
  onUpdateProject: (id: string, updates: Partial<RfqProject>) => Promise<void>;
  onOpenSubmission: (sub: RfqSubmission) => void;
  onDeleteSubmission?: (sub: RfqSubmission, project: RfqProject) => void | Promise<void>;
}

const TimberRfqSuppliersPanel: React.FC<TimberRfqSuppliersPanelProps> = ({
  project,
  projectSupplierOrgIds,
  workSummary,
  supplierOrgById,
  timberCapableOrgs,
  projectSite,
  supplierInvites,
  projectNumber,
  projectName,
  packages,
  submissionsByPackageId,
  selectedReleaseId,
  hubReleases = [],
  canStaffUploadQuote = false,
  publishBusy = false,
  onSendQuoteRequest,
  onResendScopeChange,
  onDismissScopeChange,
  rejectionTargetOrgIds,
  onRejectQuote,
  onRequestRejectionNotices,
  cncRfqProject,
  hubProjectManagerUid,
  onUpdateProject,
  onOpenSubmission,
  onDeleteSubmission,
}) => {
  const { user } = useAuth();
  const { crmUsers, rfqPackages, rfqSubmissions } = useCRM();
  const supplierBills = useSupplierBills();
  const [modalOpen, setModalOpen] = useState(false);
  const [splitTarget, setSplitTarget] = useState<{
    sub: RfqSubmission;
    orgId: string;
    orgName: string;
  } | null>(null);
  const [editTarget, setEditTarget] = useState<{
    sub: RfqSubmission;
    orgId: string;
    orgName: string;
    packageId: string;
  } | null>(null);
  const [saveBusy, setSaveBusy] = useState(false);
  const [deleteBusyId, setDeleteBusyId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{
    sub: RfqSubmission;
    orgName: string;
  } | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [approveBusyId, setApproveBusyId] = useState<string | null>(null);
  const [approvalTarget, setApprovalTarget] = useState<SupplierQuoteApprovalTarget | null>(null);
  const [approvalEmailOnly, setApprovalEmailOnly] = useState(false);
  const approvalFollowUpRef = useRef<(() => void) | null>(null);
  const [paymentTermsTarget, setPaymentTermsTarget] = useState<{
    submissionKey: string;
    vendorName: string;
    quoteLabel: string;
    amount: number;
    initialTerms?: SupplierPaymentTermsSpec | null;
    lockedInstallmentIds?: string[];
    paidInstallmentIds?: string[];
  } | null>(null);
  const [uploadTarget, setUploadTarget] = useState<{ orgId: string; orgName: string } | null>(null);
  const [timberItems, setTimberItems] = useState<TimberItem[]>([]);
  const [proposalNodeSetRows, setProposalNodeSetRows] = useState<{ id: string; enabled?: boolean }[]>([]);
  const [hubStreams, setHubStreams] = useState<{ structureIds?: string[] }[]>([]);
  /** Structures suppliers can quote (enabled proposal node sets tagged on Files tracks). */
  const rfqStructureIds = useMemo(
    () => new Set(rfqStructureNodeSets(proposalNodeSetRows, hubStreams).map(ns => ns.id)),
    [proposalNodeSetRows, hubStreams],
  );
  const proposalStructureCount = rfqStructureIds.size;
  const [expandedHistoryIds, setExpandedHistoryIds] = useState<Set<string>>(new Set());
  const [scopeDismissTarget, setScopeDismissTarget] = useState<{ orgId: string; orgName: string } | null>(
    null,
  );
  const [scopeDismissBusy, setScopeDismissBusy] = useState(false);

  const canApproveSplit = useMemo(() => {
    const row = crmUsers.find((u) => u.uid === user?.id);
    return canApproveSupplierQuoteOnBehalf({
      authRole: user?.role,
      designations: row?.designations,
      hubProjectManagerUid,
      userId: user?.id,
    });
  }, [user, crmUsers, hubProjectManagerUid]);

  const cncPackages = useMemo(
    () =>
      cncRfqProject?.id
        ? rfqPackages.filter((p) => p.rfqProjectId === cncRfqProject.id)
        : [],
    [rfqPackages, cncRfqProject?.id],
  );

  const cncSubmissions = useMemo(
    () =>
      cncRfqProject?.id
        ? rfqSubmissions.filter((s) => s.rfqProjectId === cncRfqProject.id)
        : [],
    [rfqSubmissions, cncRfqProject?.id],
  );

  const toggleHistory = (id: string) => {
    setExpandedHistoryIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  useEffect(() => subscribeToTimberItems(setTimberItems), []);

  useEffect(() => {
    if (!project.dealId) return;
    let cancelled = false;
    void (async () => {
      try {
        const snap = await getDoc(doc(db, 'proposalBuilders', project.dealId));
        if (cancelled || !snap.exists()) return;
        const nodeSets = (snap.data().nodeSets ?? []) as { id: string; enabled?: boolean }[];
        setProposalNodeSetRows(nodeSets);
      } catch {
        // Badge falls back to a plain structure count.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [project.dealId]);

  useEffect(() => {
    const hubProjectId = project.timberProjectId;
    if (!hubProjectId) {
      setHubStreams([]);
      return;
    }
    return subscribeProjectFileStreams(hubProjectId, setHubStreams);
  }, [project.timberProjectId]);

  const packagesForRelease = useMemo(
    () => filterTimberPackagesForReleaseSelection(packages, selectedReleaseId),
    [packages, selectedReleaseId],
  );

  const displaySupplierOrgIds = useMemo(() => {
    const ids = supplierOrgIdsForReleaseSelection(packagesForRelease, projectSupplierOrgIds);
    if (!rejectionTargetOrgIds?.size) return ids;
    const merged = [...ids];
    rejectionTargetOrgIds.forEach(id => {
      if (!merged.includes(id)) merged.push(id);
    });
    return merged;
  }, [packagesForRelease, projectSupplierOrgIds, rejectionTargetOrgIds]);

  const publishedReleaseIds = useMemo(() => {
    const ids = new Set<string>();
    packages.forEach(p => {
      for (const id of packageHubReleaseIds(p)) ids.add(id);
    });
    return ids;
  }, [packages]);

  const selectedReleasePublished =
    !!selectedReleaseId
    && selectedReleaseId !== PRE_PORTAL_RELEASE_ID
    && publishedReleaseIds.has(selectedReleaseId);

  const approvalReleaseId = selectedReleaseId ?? PRE_PORTAL_RELEASE_ID;

  const approvedSubmissionIds = useMemo(
    () =>
      new Set(
        approvedSubmissionIdsForReleaseSelection(
          project,
          packages,
          submissionsByPackageId,
          selectedReleaseId,
        ),
      ),
    [project, packages, submissionsByPackageId, selectedReleaseId],
  );

  const packageByOrgId = useMemo(() => {
    const m = new Map<string, RfqPackage>();
    packages
      .slice()
      .sort((a, b) => {
        const ta = a.createdAt instanceof Date ? a.createdAt.getTime() : 0;
        const tb = b.createdAt instanceof Date ? b.createdAt.getTime() : 0;
        return tb - ta;
      })
      .forEach(p => {
        if (p.supplierOrgId && !m.has(p.supplierOrgId)) m.set(p.supplierOrgId, p);
      });
    packagesForRelease.forEach(p => {
      if (p.supplierOrgId) m.set(p.supplierOrgId, p);
    });
    return m;
  }, [packages, packagesForRelease]);

  const allSubs = useMemo(() => {
    const list: RfqSubmission[] = [];
    for (const orgId of displaySupplierOrgIds) {
      const pkg = packageByOrgId.get(orgId);
      if (pkg?.id) list.push(...(submissionsByPackageId.get(pkg.id) ?? []));
    }
    return list;
  }, [displaySupplierOrgIds, packageByOrgId, submissionsByPackageId]);

  const speciesOptions = useMemo(() => {
    const extras: string[] = [];
    for (const sub of allSubs) {
      const tagged = sub.quotedSpecies?.trim();
      if (tagged) extras.push(tagged);
      else if (sub.fileName) {
        const inferred = inferQuotedSpeciesFromFileName(sub.fileName, []);
        if (inferred) extras.push(inferred);
      }
    }
    return allSelectableTimberSpecies(timberItems, extras);
  }, [timberItems, allSubs]);

  const handleSaveSuppliers = async (updates: {
    supplierOrgIds: string[];
    supplierOrgId: string;
    supplierOrgName?: string;
    timberSupplierInvites: TimberSupplierInvite[];
    approvedSupplierOrgId?: string;
  }) => {
    if (!project.id) return;
    setSaveBusy(true);
    try {
      await onUpdateProject(project.id, buildRfqSupplierSaveUpdates(updates) as Partial<RfqProject>);
    } finally {
      setSaveBusy(false);
    }
  };

  const requestDeleteSubmission = (sub: RfqSubmission, orgName: string) => {
    if (!sub.id || !onDeleteSubmission) return;
    setDeleteError(null);
    setDeleteTarget({ sub, orgName });
  };

  const handleDeleteSubmission = async () => {
    const sub = deleteTarget?.sub;
    if (!sub?.id || !onDeleteSubmission) return;
    setDeleteBusyId(sub.id);
    setDeleteError(null);
    try {
      await onDeleteSubmission(sub, project);
      if (project.id && approvedSubmissionIds.has(sub.id)) {
        await onUpdateProject(project.id, buildTimberApprovalUpdate(project, approvalReleaseId, sub.id, false));
        void retractSupplierBillForRfqAward({
          project,
          submissionKey: sub.id,
          byUserId: user?.id || '',
          byUserName: user?.name || user?.email || '',
        });
      }
      setDeleteTarget(null);
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : 'Could not delete this quote.');
    } finally {
      setDeleteBusyId(null);
    }
  };

  const handleApproveQuote = (
    sub: RfqSubmission,
    orgId: string,
    orgName: string,
    emailOnly = false,
  ) => {
    if (!project.id || !sub.id) return;
    const species =
      submissionQuotedSpecies(sub, speciesOptions) ??
      inferQuotedSpeciesFromFileName(sub.fileName ?? '', speciesOptions) ??
      sub.fileName ??
      'Quote';
    const total = formatQuoteTotal(submissionQuoteTotal(sub));
    const quoteLabel = total !== '—' ? `${species} — ${total}` : species;
    approvalFollowUpRef.current = null;
    setApprovalEmailOnly(emailOnly);
    setApprovalTarget({ kind: 'timber', orgId, orgName, sub, quoteLabel });
  };

  const timberQuoteStreamKeyFor = (sub: RfqSubmission) =>
    timberQuoteStreamKey(sub, (s) =>
      submissionQuotedSpecies(s, speciesOptions)
      ?? inferQuotedSpeciesFromFileName(s.fileName ?? '', speciesOptions)
      ?? s.quotedSpecies
      ?? '',
    );

  const handleConfirmApproval = async () => {
    if (!approvalTarget || approvalTarget.kind !== 'timber' || !project.id) return;
    const sub = approvalTarget.sub;
    if (!sub.id) return;
    const approverInitials = getUserInitials(user?.name || user?.email || '');
    const pkgSubs =
      sub.rfqPackageId ? (submissionsByPackageId.get(sub.rfqPackageId) ?? []) : [];
    const activeSubs = activeQuoteSubmissions(pkgSubs);
    const streamSiblings = siblingSubmissionIdsInQuoteStream(
      sub,
      activeSubs,
      timberQuoteStreamKeyFor,
    );
    const pkg =
      (sub.rfqPackageId && packages.find((p) => p.id === sub.rfqPackageId))
      || packages.find((p) => p.supplierOrgId === approvalTarget.orgId);
    const assignedStructureIds = timberQuoteAssignedStructureIds(
      project,
      approvalTarget.orgId,
      pkg,
    );
    const overlapClear = approvalClearIdsForTimberQuote({
      approving: sub,
      candidates: activeSubs,
      assignedStructureIds,
    });
    const clearSubmissionIds = Array.from(new Set([...streamSiblings, ...overlapClear]));
    setApproveBusyId(sub.id);
    try {
      const awardUpdate = buildTimberApprovalUpdate(
        project,
        approvalReleaseId,
        sub.id,
        true,
        approverInitials,
        { clearSubmissionIds },
      );
      await onUpdateProject(project.id, awardUpdate);
      try {
        await recordSupplierBillForRfqAward(
          {
            project,
            category: 'timber',
            orgId: approvalTarget.orgId,
            orgName: approvalTarget.orgName,
            submissionKey: sub.id,
            amount: submissionQuoteTotal(sub),
            description: `Timber quote — ${approvalTarget.quoteLabel}`,
            byUserId: user?.id || '',
            byUserName: user?.name || user?.email || '',
          },
          { throwOnError: true },
        );
        const pendingTerms = {
          submissionKey: sub.id,
          vendorName: approvalTarget.orgName,
          quoteLabel: approvalTarget.quoteLabel,
          amount: submissionQuoteTotal(sub) ?? 0,
        };
        const nextProject = { ...project, ...awardUpdate };
        const rejectionTargets = buildQuoteRejectionTargets({
          workType: 'timber_rfq',
          project: nextProject,
          projectSupplierOrgIds,
          packages,
          submissionsByPackageId,
          selectedReleaseId: approvalReleaseId,
          supplierOrgById,
        });
        const openTerms = () => setPaymentTermsTarget(pendingTerms);
        approvalFollowUpRef.current = () => {
          if (rejectionTargets.length && onRequestRejectionNotices) {
            onRequestRejectionNotices(rejectionTargets, openTerms);
          } else {
            openTerms();
          }
        };
      } catch (err) {
        window.alert(
          err instanceof Error
            ? err.message
            : 'Quote was approved but the Accounts Payable bill could not be created. It may appear after sync.',
        );
      }
    } finally {
      setApproveBusyId(null);
    }
  };

  const handleSavePaymentTerms = async (terms: SupplierPaymentTermsSpec) => {
    if (!paymentTermsTarget || !project.id) return;
    const bill = await findSupplierBillForRfqAward(project.id, paymentTermsTarget.submissionKey);
    if (!bill?.id) {
      throw new Error('Bill not found yet — set terms from Billing & Payables once it appears.');
    }
    await setSupplierBillPaymentTerms(bill.id, terms, {
      expectedRfqSubmissionId: paymentTermsTarget.submissionKey,
      allowRaiseBillAmount: true,
    });
  };

  return (
    <div className="srfq-timber-suppliers-panel">
      <div className="srfq-timber-specs-bar">
        <span className="srfq-timber-specs-static">
          Dimensions: {timberSupplierDimensionsSpecForLists(
            (selectedReleaseId ? hubReleases.filter((release) => release.id === selectedReleaseId) : hubReleases).map(
              (release) => ({
                trueDimensions: release.trueDimensions === true,
                label: release.displayLabel || release.fileName,
              }),
            ),
          )}
        </span>
        <span className="srfq-timber-specs-static">Surface finish: {TIMBER_SUPPLIER_SURFACE_SPEC}</span>
      </div>

      {displaySupplierOrgIds.length === 0 ? (
        <p className="srfq-muted srfq-timber-suppliers-empty">No suppliers invited yet.</p>
      ) : (
        <ul className="srfq-timber-supplier-cards">
          {displaySupplierOrgIds.map(orgId => {
            const onCurrentRoster = projectSupplierOrgIds.includes(orgId);
            const orgName = supplierOrgById.get(orgId)?.name || orgId;
            const pkg = packageByOrgId.get(orgId);
            const subs = (pkg?.id && submissionsByPackageId.get(pkg.id)) || [];
            const quoteSubs = activeQuoteSubmissions(subs);
            // Latest quote per species + structure-set stream; older uploads are history.
            const quoteGroups = groupQuoteSubmissionHistory(quoteSubs, timberQuoteStreamKeyFor);
            const latestQuoteSubs = primarySubmissionsFromGroups(quoteGroups, approvedSubmissionIds);
            const quoteEntries = quoteGroups.flatMap(group =>
              visibleQuoteRowsNewestFirst(
                resolveQuoteStreamDisplay(group, approvedSubmissionIds),
                approvedSubmissionIds,
              ),
            );
            const latestSub = subs[0];
            const hasQuote = quoteGroups.length > 0;
            const combinedTotal = latestQuoteSubs.reduce((sum, sub) => {
              const t = submissionQuoteTotal(sub);
              return t != null ? sum + t : sum;
            }, 0);
            const approvedCount = latestQuoteSubs.filter(s => s.id && approvedSubmissionIds.has(s.id)).length;
            const assignedStructureIds = timberQuoteAssignedStructureIds(project, orgId, pkg);
            const approvedPrimaries = latestQuoteSubs.filter(
              (s) => s.id && approvedSubmissionIds.has(s.id),
            );
            const allQuotesApproved = timberAssignedStructuresFullyCovered({
              approvedSubs: approvedPrimaries,
              assignedStructureIds,
              primaryStreamCount: latestQuoteSubs.length,
            });
            const hasPendingRevisions = quoteEntries.some(entry => entry.isPendingRevision);
            const cardFullyApproved = allQuotesApproved && !hasPendingRevisions;
            const hasPartialApproval = approvedCount > 0 && !cardFullyApproved;

            const invite = project.timberSupplierInvites?.find(row => row.supplierOrgId === orgId);
            const structureBadge = supplierStructureScopeBadgeLabel(
              invite,
              proposalStructureCount,
              pkg,
            );
            const openStructureNames = pkg?.scopeStructures?.length
              ? activeScopeStructures(pkg.scopeStructures, pkg.archivedStructureIds).map((s) => s.name).filter(Boolean)
              : (invite?.structures ?? []).map((s) => s.name).filter(Boolean);
            const archivedStructureNames = (pkg?.scopeStructures ?? [])
              .filter((s) => s.id && (pkg?.archivedStructureIds ?? []).includes(s.id))
              .map((s) => s.name)
              .filter(Boolean);
            const structureBadgeTitle = archivedStructureNames.length
              ? `${openStructureNames.join(', ') || 'No structures still open'}. Archived: ${archivedStructureNames.join(', ')}. A later RFQ covers those structures, so the supplier is not asked to quote them on this request.`
              : openStructureNames.join(', ');

            const needsQuoteRequest =
              onCurrentRoster
              && !pkg
              && selectedReleasePublished
              && !!onSendQuoteRequest;
            const scopeChanged =
              onCurrentRoster
              && !!pkg
              && !pkg.legacyImport
              && !isReferenceRelease(pkg)
              && selectedReleasePublished
              && (!!onResendScopeChange || !!onDismissScopeChange)
              && packageScopeDiffersFromInvite(pkg, invite, proposalStructureCount);
            const canRejectQuote =
              (!!onRequestRejectionNotices || !!onRejectQuote)
              && user?.role !== 'staff';

            return (
              <li
                key={orgId}
                className={`srfq-timber-supplier-card ${
                  cardFullyApproved ? 'approved' : hasPartialApproval ? 'approved-partial' : hasQuote ? 'quoted' : ''
                }${!onCurrentRoster ? ' srfq-timber-supplier-card--prior-release' : ''}`}
              >
                <div className="srfq-timber-supplier-card-head">
                  <div className="srfq-timber-supplier-card-head-main">
                    <div className="srfq-timber-supplier-card-title">
                      {cardFullyApproved && <Check size={12} aria-hidden />}
                      <span className="srfq-timber-supplier-card-name">{orgName}</span>
                      {structureBadge && (
                        <span
                          className="srfq-timber-supplier-structures-badge"
                          title={structureBadgeTitle || undefined}
                        >
                          {structureBadge}
                        </span>
                      )}
                    </div>
                    <div className="srfq-timber-supplier-card-statuses">
                      {cardFullyApproved ? (
                        <span className="srfq-timber-supplier-status approved">Approved</span>
                      ) : hasPartialApproval && hasPendingRevisions ? (
                        <>
                          <span className="srfq-timber-supplier-status approved">Approved</span>
                          <span className="srfq-timber-supplier-status revision-pending">Revision pending</span>
                        </>
                      ) : approvedCount > 0 ? (
                        <span className="srfq-timber-supplier-status quoted">
                          {approvedCount}/{latestQuoteSubs.length} approved
                        </span>
                      ) : hasQuote ? (
                        <span className="srfq-timber-supplier-status quoted">Quoted</span>
                      ) : pkg ? (
                        <span className="srfq-timber-supplier-status published">Published</span>
                      ) : !onCurrentRoster ? (
                        <span className="srfq-timber-supplier-status prior-release">Prior release</span>
                      ) : needsQuoteRequest ? (
                        <span className="srfq-timber-supplier-status invited">Not sent</span>
                      ) : null}
                      {scopeChanged && (
                        <span
                          className="srfq-timber-supplier-status scope-changed"
                          title="This supplier's structures or species per structure no longer match their published quote request"
                        >
                          Scope changed
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="srfq-timber-supplier-card-toolbar">
                    {needsQuoteRequest && (
                      <button
                        type="button"
                        className="srfq-timber-supplier-send-rfq"
                        disabled={publishBusy}
                        onClick={() => onSendQuoteRequest?.([orgId])}
                      >
                        <Send size={12} aria-hidden />
                        Send quote request
                      </button>
                    )}
                    {scopeChanged && (
                      <div className="srfq-scope-change-actions">
                        {onResendScopeChange && (
                          <button
                            type="button"
                            className="srfq-timber-supplier-send-rfq srfq-timber-supplier-resend-scope"
                            disabled={publishBusy}
                            onClick={() => onResendScopeChange(orgId)}
                          >
                            <Send size={12} aria-hidden />
                            Resend — scope changed
                          </button>
                        )}
                        {onDismissScopeChange && (
                          <button
                            type="button"
                            className="srfq-scope-dismiss-btn"
                            disabled={publishBusy || scopeDismissBusy}
                            aria-label={`Dismiss scope change reminder for ${orgName}`}
                            title="Dismiss reminder"
                            onClick={() => setScopeDismissTarget({ orgId, orgName })}
                          >
                            <X size={12} aria-hidden />
                          </button>
                        )}
                      </div>
                    )}
                    {canStaffUploadQuote && project.id && (
                      <button
                        type="button"
                        className="srfq-staff-upload-quote-btn srfq-staff-upload-quote-btn--inline"
                        onClick={() => setUploadTarget({ orgId, orgName })}
                      >
                        <FileUp size={12} aria-hidden />
                        Upload quote PDF
                      </button>
                    )}
                  </div>
                </div>

                <SupplierShipToLine
                  address={rfqProjectSupplierShipTo(project, orgId, {
                    ...shipToContextForWorkType('timber_rfq', project, cncRfqProject),
                    orgId,
                    projectSite,
                  })}
                />

                {hasQuote && combinedTotal > 0 && quoteEntries.length <= 1 && (
                  <p className="srfq-timber-supplier-quote-total">
                    {latestQuoteSubs.length > 1 ? `${formatQuoteTotal(combinedTotal)} total` : formatQuoteTotal(combinedTotal)}
                  </p>
                )}

                {quoteEntries.length > 0 && (
                  <ul className="srfq-timber-quote-rows" aria-label={`Quotes from ${orgName}`}>
                    {quoteEntries.map(({ sub, isPendingRevision, previous, showPreviousHistory, canonicalApprovedId }) => {
                      const species =
                        submissionQuotedSpecies(sub, speciesOptions) ??
                        inferQuotedSpeciesFromFileName(sub.fileName ?? '', speciesOptions) ??
                        '';
                      const drynessSpec = submissionQuotedDryness(sub, project, orgId, species);
                      const isRejected = isRfqQuoteRejected(sub);
                      const isQuoteApproved =
                        !isPendingRevision
                        && !isRejected
                        && !!sub.id
                        && sub.id === canonicalApprovedId;
                      const canEdit =
                        canInternalEditQuoteSubmission(sub, pkg?.id, canStaffUploadQuote)
                        || canStaffEditQuoteSubmission(sub, pkg?.id);
                      const amountValue = submissionQuoteTotal(sub);
                      const leadTimeLabel = formatSubmissionLeadTimeWeeks(sub);
                      const quotedUnderPreviousScope =
                        pkg?.scopeRevisedAt instanceof Date
                        && sub.createdAt instanceof Date
                        && sub.createdAt.getTime() < pkg.scopeRevisedAt.getTime();

                      return (
                        <li
                          key={sub.id ?? sub.storagePath}
                          data-rfq-submission-id={sub.id || undefined}
                          className={`srfq-timber-quote-row${isQuoteApproved ? ' approved' : ''}${
                            isPendingRevision ? ' pending-revision' : ''
                          }${isRejected ? ' rejected' : ''}${
                            !isQuoteApproved && !isPendingRevision && !isRejected ? ' awaiting-approval' : ''
                          }`}
                        >
                          {isPendingRevision && (
                            <div className="srfq-timber-quote-row-banner srfq-timber-quote-row-banner--pending">
                              Revised quote — review &amp; approve
                            </div>
                          )}
                          {isQuoteApproved && (
                            <ApprovedQuoteBanner
                              meta={resolveApprovedQuoteBannerMeta({
                                approvedAt: timberQuoteApprovedAt(project, sub.id),
                                initials: timberQuoteApproverInitials(project, sub.id),
                                bill: findAwardBillForSubmission(supplierBills, project.id, sub.id),
                                dealId: project.dealId,
                              })}
                              onSendApprovalEmail={
                                canApproveSplit && project.id && sub.id
                                  ? () => handleApproveQuote(sub, orgId, orgName, true)
                                  : undefined
                              }
                              onEditPaymentTerms={
                                canApproveSplit && project.id && sub.id
                                  ? () => {
                                      const amountValue = submissionQuoteTotal(sub);
                                      const quoteLabel = species
                                        ? `Timber quote — ${species} — ${formatQuoteTotal(amountValue ?? 0)}`
                                        : `Timber quote — ${orgName}`;
                                      setPaymentTermsTarget(
                                        buildApprovedQuotePaymentTermsTarget({
                                          submissionKey: sub.id!,
                                          vendorName: orgName,
                                          quoteLabel,
                                          quoteAmount: amountValue,
                                          bill: findAwardBillForSubmission(
                                            supplierBills,
                                            project.id,
                                            sub.id,
                                          ),
                                        }),
                                      );
                                    }
                                  : undefined
                              }
                            />
                          )}
                          {isRejected && (
                            <div className="srfq-timber-quote-row-banner srfq-timber-quote-row-banner--rejected">
                              Rejected quote
                            </div>
                          )}
                          <div className="srfq-timber-quote-row-header">
                            <div className="srfq-timber-quote-row-header-left">
                              <span className="srfq-timber-species-badge">
                                {species || 'Quote'}
                              </span>
                              <span className="srfq-timber-quote-structure-scope">
                                {timberQuoteStructureScopeDisplayLabel(
                                  sub,
                                  timberQuoteAssignableStructures(project, orgId, pkg),
                                  proposalStructureCount,
                                )}
                              </span>
                              {species && (
                                <span className="srfq-timber-quote-dryness">
                                  {timberRfqDrynessSpecLabel(drynessSpec)}
                                </span>
                              )}
                              <PmSubmissionQuoteInputAdjustedBadge submission={sub} />
                              {isStaffManualUploadSubmission(sub) && (
                                <span className="srfq-manual-upload-badge srfq-manual-upload-badge--inline">
                                  {staffManualUploadBadgeLabel(sub)}
                                </span>
                              )}
                              {isLegacyPrePortalSubmission(sub) && (
                                <span className="srfq-legacy-import-badge srfq-legacy-import-badge--inline">
                                  Pre-portal
                                </span>
                              )}
                              {!!sub.linkedCncSubmissionId && (
                                <span
                                  className="cqs-split-badge"
                                  title={
                                    sub.combinedQuoteTotal != null
                                      ? `Split from combined ${formatQuoteTotal(sub.combinedQuoteTotal)}`
                                      : 'CNC split from this quote'
                                  }
                                >
                                  CNC split
                                </span>
                              )}
                              {quotedUnderPreviousScope && (
                                <span
                                  className="srfq-timber-quote-prev-scope-badge"
                                  title="Submitted before the structure scope was updated — the price may not match the current scope"
                                >
                                  Quoted under previous scope
                                </span>
                              )}
                              {amountValue != null && (
                                <span className="srfq-timber-species-quote-total">
                                  {formatQuoteTotal(amountValue)}
                                </span>
                              )}
                              {leadTimeLabel && (
                                <span className="srfq-timber-quote-lead-time">
                                  {leadTimeLabel}
                                </span>
                              )}
                              {sub.notes?.trim() && (
                                <span className="srfq-timber-quote-notes" title={sub.notes.trim()}>
                                  {sub.notes.trim()}
                                </span>
                              )}
                            </div>
                            <div className="srfq-timber-quote-row-actions">
                              {canEdit && (
                                <button
                                  type="button"
                                  className="srfq-quote-action-btn srfq-quote-action-btn--edit"
                                  aria-label={`Edit ${species || 'quote'}`}
                                  title="Edit quote"
                                  onClick={() =>
                                    setEditTarget({
                                      sub,
                                      orgId,
                                      orgName,
                                      packageId: pkg!.id!,
                                    })
                                  }
                                >
                                  <Pencil size={14} aria-hidden />
                                </button>
                              )}
                              <button
                                type="button"
                                className="srfq-quote-action-btn srfq-quote-action-btn--open"
                                aria-label={`Open ${species || 'quote'} PDF`}
                                title="Open PDF"
                                onClick={() => onOpenSubmission(sub)}
                              >
                                <ExternalLink size={14} aria-hidden />
                              </button>
                              {(() => {
                                const showSplit = canShowCombinedQuoteSplitEntry({
                                  canApprove: canApproveSplit,
                                  isPendingRevision,
                                  isQuoteApproved,
                                  orgId,
                                  org: supplierOrgById.get(orgId),
                                  timberProject: project,
                                  cncProject: cncRfqProject,
                                  timberSubmission: sub,
                                  cncSubmissionsForOrg: cncSubmissions.filter(
                                    (s) => s.supplierOrgId === orgId,
                                  ),
                                });
                                if (!showSplit) return null;
                                return (
                                  <button
                                    type="button"
                                    className="cqs-split-cue-btn"
                                    aria-label={`Split CNC from ${species || 'quote'}`}
                                    title="Split CNC from quote"
                                    onClick={() => setSplitTarget({ sub, orgId, orgName })}
                                  >
                                    <Split size={14} aria-hidden />
                                    Split CNC
                                  </button>
                                );
                              })()}
                              {!isQuoteApproved && !isRejected && canApproveSplit && project.id && sub.id && (
                                <button
                                  type="button"
                                  className="srfq-quote-action-btn srfq-quote-action-btn--approve"
                                  aria-label={`Approve ${species || 'quote'}`}
                                  title="Approve quote"
                                  disabled={approveBusyId === sub.id}
                                  onClick={() => handleApproveQuote(sub, orgId, orgName)}
                                >
                                  <CheckCircle2 size={14} aria-hidden />
                                </button>
                              )}
                              {isQuoteApproved && !isPendingRevision && canApproveSplit && project.id && sub.id && (
                                <button
                                  type="button"
                                  className="srfq-quote-action-btn srfq-quote-action-btn--unapprove"
                                  aria-label={`Undo approval for ${species || 'quote'}`}
                                  title="Undo approval"
                                  onClick={() => {
                                    void onUpdateProject(
                                      project.id!,
                                      buildTimberApprovalUpdate(project, approvalReleaseId, sub.id!, false),
                                    );
                                    void retractSupplierBillForRfqAward({
                                      project,
                                      submissionKey: sub.id!,
                                      byUserId: user?.id || '',
                                      byUserName: user?.name || user?.email || '',
                                    });
                                  }}
                                >
                                  <RotateCcw size={14} aria-hidden />
                                </button>
                              )}
                              {canRejectQuote
                              && !isRejected
                              && !isQuoteApproved
                              && !!sub.id
                              && (
                                isLegacyPrePortalSubmission(sub)
                                || !!rejectionTargetOrgIds?.has(orgId)
                              ) && (
                                <button
                                  type="button"
                                  className="srfq-quote-action-btn srfq-quote-action-btn--reject srfq-quote-action-btn--reject-label"
                                  aria-label={`Decline quote from ${orgName}`}
                                  title="Decline quote"
                                  onClick={() => {
                                    if (onRequestRejectionNotices) {
                                      onRequestRejectionNotices([
                                        timberQuoteDeclineTarget(sub, orgId, orgName, pkg),
                                      ]);
                                      return;
                                    }
                                    onRejectQuote?.(orgId);
                                  }}
                                >
                                  <Ban size={14} aria-hidden />
                                  <span>Decline</span>
                                </button>
                              )}
                              {onDeleteSubmission && sub.id && (
                                <button
                                  type="button"
                                  className="srfq-quote-action-btn srfq-quote-action-btn--delete"
                                  disabled={deleteBusyId === sub.id}
                                  aria-label={`Delete ${species || 'quote'}`}
                                  title="Delete quote"
                                  onClick={() => requestDeleteSubmission(sub, orgName)}
                                >
                                  <Trash2 size={14} aria-hidden />
                                </button>
                              )}
                            </div>
                          </div>
                          {showPreviousHistory && previous.length > 0 && (() => {
                            const historyId = sub.id ?? sub.storagePath;
                            const historyOpen = expandedHistoryIds.has(historyId);
                            return (
                              <div className="srfq-quote-history">
                                <button
                                  type="button"
                                  className="srfq-quote-history-toggle"
                                  aria-expanded={historyOpen}
                                  onClick={() => toggleHistory(historyId)}
                                >
                                  {historyOpen ? (
                                    <ChevronDown size={12} aria-hidden />
                                  ) : (
                                    <ChevronRight size={12} aria-hidden />
                                  )}
                                  {previous.length === 1
                                    ? '1 previous quote'
                                    : `${previous.length} previous quotes`}
                                </button>
                                {historyOpen && (
                                  <ul
                                    className="srfq-quote-history-list"
                                    aria-label={`Previous ${species || 'quote'} quotes from ${orgName}`}
                                  >
                                    {previous.map(prev => {
                                      const prevAmount = submissionQuoteTotal(prev);
                                      return (
                                        <li key={prev.id ?? prev.storagePath} className="srfq-quote-history-row">
                                          {prevAmount != null && (
                                            <span className="srfq-quote-history-price">
                                              {formatQuoteTotal(prevAmount)}
                                            </span>
                                          )}
                                          {formatSubmissionLeadTimeWeeks(prev) && (
                                            <span className="srfq-timber-quote-lead-time">
                                              {formatSubmissionLeadTimeWeeks(prev)}
                                            </span>
                                          )}
                                          {prev.notes?.trim() && (
                                            <span className="srfq-timber-quote-notes" title={prev.notes.trim()}>
                                              {prev.notes.trim()}
                                            </span>
                                          )}
                                          {prev.createdAt instanceof Date && (
                                            <span className="srfq-quote-history-date">
                                              {prev.createdAt.toLocaleDateString(undefined, {
                                                month: 'short',
                                                day: 'numeric',
                                                year: 'numeric',
                                              })}
                                            </span>
                                          )}
                                          <span className="srfq-quote-history-actions">
                                            {prev.storagePath && (
                                              <button
                                                type="button"
                                                className="srfq-quote-action-btn srfq-quote-action-btn--open"
                                                aria-label="Open previous quote PDF"
                                                title="Open PDF"
                                                onClick={() => onOpenSubmission(prev)}
                                              >
                                                <ExternalLink size={14} aria-hidden />
                                              </button>
                                            )}
                                            {project.id && prev.id && user?.role !== 'staff' && !approvedSubmissionIds.has(prev.id) && (
                                              <button
                                                type="button"
                                                className="srfq-quote-action-btn srfq-quote-action-btn--approve"
                                                aria-label="Approve previous quote"
                                                title="Approve quote"
                                                disabled={approveBusyId === prev.id}
                                                onClick={() => handleApproveQuote(prev, orgId, orgName)}
                                              >
                                                <CheckCircle2 size={14} aria-hidden />
                                              </button>
                                            )}
                                            {onDeleteSubmission && prev.id && (
                                              <button
                                                type="button"
                                                className="srfq-quote-action-btn srfq-quote-action-btn--delete"
                                                disabled={deleteBusyId === prev.id}
                                                aria-label="Delete previous quote"
                                                title="Delete quote"
                                                onClick={() => requestDeleteSubmission(prev, orgName)}
                                              >
                                                <Trash2 size={14} aria-hidden />
                                              </button>
                                            )}
                                          </span>
                                        </li>
                                      );
                                    })}
                                  </ul>
                                )}
                              </div>
                            );
                          })()}
                        </li>
                      );
                    })}
                  </ul>
                )}

                {latestSub?.notBidding && (
                  <div className="srfq-timber-supplier-card-actions">
                    <span className="srfq-muted">Not bidding</span>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <button
        type="button"
        className="ops-hide-completed-btn srfq-timber-suppliers-manage"
        disabled={saveBusy || !project.id || timberCapableOrgs.length === 0}
        onClick={() => setModalOpen(true)}
      >
        <Plus size={13} aria-hidden />
        {projectSupplierOrgIds.length ? 'Manage suppliers…' : 'Add suppliers…'}
      </button>

      {editTarget && project.id && (
        <LegacyQuoteEditModal
          open
          project={project}
          submission={editTarget.sub}
          supplierOrgId={editTarget.orgId}
          supplierName={editTarget.orgName}
          packageId={editTarget.packageId}
          releaseId={approvalReleaseId}
          onClose={() => setEditTarget(null)}
        />
      )}

      {splitTarget && project.id && (
        <CombinedQuoteSplitModal
          open
          timberSubmission={splitTarget.sub}
          timberProject={project}
          cncProject={cncRfqProject ?? null}
          orgId={splitTarget.orgId}
          orgName={splitTarget.orgName}
          packages={packages}
          submissionsByPackageId={submissionsByPackageId}
          selectedTimberReleaseId={selectedReleaseId}
          cncPackagesForOrg={cncPackages.filter((p) => p.supplierOrgId === splitTarget.orgId)}
          cncSubmissionsForOrg={cncSubmissions.filter((s) => s.supplierOrgId === splitTarget.orgId)}
          hubProjectManagerUid={hubProjectManagerUid}
          onClose={() => setSplitTarget(null)}
        />
      )}

      {uploadTarget && project.id && (
        <StaffSupplierQuoteUploadModal
          open
          project={project}
          supplierOrgId={uploadTarget.orgId}
          supplierName={uploadTarget.orgName}
          workType={'timber_rfq' as VendorWorkType}
          selectedReleaseId={selectedReleaseId}
          hubReleases={hubReleases}
          projectPackages={packages}
          dealStructureCount={proposalStructureCount}
          onClose={() => setUploadTarget(null)}
        />
      )}

      {modalOpen && project.id && (
        <TimberRfqSuppliersModal
          project={project}
          projectSupplierOrgIds={projectSupplierOrgIds}
          timberCapableOrgs={timberCapableOrgs}
          projectSite={projectSite}
          cncRfqProject={cncRfqProject}
          rfqStructureIds={rfqStructureIds}
          onClose={() => setModalOpen(false)}
          onSave={handleSaveSuppliers}
        />
      )}

      <SupplierQuoteApprovalModal
        open={!!approvalTarget}
        target={approvalTarget}
        project={project}
        workType="timber_rfq"
        projectNumber={projectNumber}
        projectName={projectName}
        supplierOrgById={supplierOrgById}
        emailOnly={approvalEmailOnly}
        onClose={() => {
          setApprovalTarget(null);
          setApprovalEmailOnly(false);
        }}
        onApproved={handleConfirmApproval}
        onFinished={() => {
          const next = approvalFollowUpRef.current;
          approvalFollowUpRef.current = null;
          next?.();
        }}
      />

      <SupplierPaymentTermsModal
        open={!!paymentTermsTarget}
        vendorName={paymentTermsTarget?.vendorName ?? ''}
        quoteLabel={paymentTermsTarget?.quoteLabel ?? ''}
        billAmount={paymentTermsTarget?.amount ?? 0}
        projectId={project.timberProjectId || undefined}
        billCategory="timber"
        initialTerms={paymentTermsTarget?.initialTerms ?? null}
        lockedInstallmentIds={paymentTermsTarget?.lockedInstallmentIds}
        paidInstallmentIds={paymentTermsTarget?.paidInstallmentIds}
        onSave={handleSavePaymentTerms}
        onClose={() => setPaymentTermsTarget(null)}
      />

      <RfqQuoteDeleteConfirmModal
        open={!!deleteTarget}
        target={
          deleteTarget
            ? {
                quoteLabel: rfqSubmissionDeleteLabel(deleteTarget.sub),
                vendorName: deleteTarget.orgName,
                amount: submissionQuoteTotal(deleteTarget.sub),
              }
            : null
        }
        busy={!!deleteBusyId}
        error={deleteError}
        onClose={() => {
          if (!deleteBusyId) {
            setDeleteTarget(null);
            setDeleteError(null);
          }
        }}
        onConfirm={() => void handleDeleteSubmission()}
      />

      <ScopeChangeDismissConfirmModal
        open={!!scopeDismissTarget}
        supplierName={scopeDismissTarget?.orgName ?? ''}
        busy={scopeDismissBusy}
        onClose={() => {
          if (!scopeDismissBusy) setScopeDismissTarget(null);
        }}
        onConfirm={async () => {
          if (!scopeDismissTarget || !onDismissScopeChange) return;
          setScopeDismissBusy(true);
          try {
            const ok = await onDismissScopeChange(scopeDismissTarget.orgId);
            if (ok) setScopeDismissTarget(null);
          } finally {
            setScopeDismissBusy(false);
          }
        }}
      />
    </div>
  );
};

export default TimberRfqSuppliersPanel;
